import axios from 'axios';
import React, { useState } from 'react'
import { Bar, Line } from 'react-chartjs-2';
import { MdAutorenew, MdDeleteOutline } from 'react-icons/md';
import { toast } from 'react-toastify';

const ItemListTable = ({
    itemdata,
    debitNoteMap,
    unmatchedDebitNotes,
    costCenters,
    totalQty,
    totalAmount,
    costCenterQty,
    chartData,
    barchartdata,
    formatLocalDate,
    formatSalesValue,
    baseUrl,
    token,
    fetchData,
}) => {
    const [showCostCenterModal, setShowCostCenterModal] = useState(false);
    const [selectedReq, setSelectedReq] = useState(null);
    const [newCostCenter, setNewCostCenter] = useState("");
    const [loading, setLoading] = useState(false);

    const updateCostCenter = async () => {
        if (!newCostCenter?.trim()) {
            toast.warning("Please enter a cost center");
            return;
        }

        try {
            setLoading(true);

            const { data } = await axios.put(
                `${baseUrl}/update_cost_center`,
                {
                    token,
                    store_req_id: selectedReq?.StoreReqID,
                    cost_center: newCostCenter.trim(),
                }
            );

            toast.success(
                data?.message || "Cost center updated successfully"
            );

            setShowCostCenterModal(false);

            fetchData();
        } catch (error) {
            console.error("Update Cost Center Error:", error);

            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Failed to update cost center. Please try again.";

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const deleteStockTransfer = () => {
        toast.info(
            ({ closeToast }) => (
                <div>
                    <p className="font-medium">
                        Are you sure you want to delete this stock transfer?
                    </p>

                    <div className="flex gap-2 mt-3 justify-end">
                        <button
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 transition-colors text-black"
                            onClick={() => closeToast()}
                        >
                            Cancel
                        </button>

                        <button
                            className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                            onClick={async () => {
                                closeToast();

                                try {
                                    setLoading(true);

                                    await axios.delete(`${baseUrl}/deleteStockTransfer/${selectedReq?.StoreReqID}`);

                                    toast.success("Stock transfer deleted successfully");
                                    setShowCostCenterModal(false);
                                    fetchData();
                                } catch (error) {
                                    toast.error(
                                        error?.response?.data?.error ||
                                        error?.response?.data?.message ||
                                        "Failed to delete. Please try again."
                                    );
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            ),
            {
                autoClose: false,
                closeOnClick: false,
                closeButton: false,
                draggable: false,
            }
        );
    };

    return (
        <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-10 my-4">

                <div>
                    {/* ================= LEFT: ITEM TABLE ================= */}
                    <h2 className="text-base font-semibold text-[#003366]">
                        Store Purchase
                    </h2>
                    <div className="bg-white shadow-md rounded-md border overflow-hidden">
                        <div className="overflow-x-auto overflow-y-auto max-h-[550px] tiny-scrollbar">
                            <table className="min-w-full text-sm text-center">

                                {/* Header */}
                                <thead className="bg-slate-100 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-3 py-2.5">Date</th>
                                        <th className="px-3 py-2.5">Vendor</th>
                                        <th className="px-3 py-2.5">Qty</th>
                                        <th className="px-3 py-2.5">Rate</th>
                                        <th className="px-3 py-2.5">Total</th>
                                        <th className="px-3 py-2.5">GRN</th>
                                    </tr>
                                </thead>

                                {/* Body */}
                                <tbody>
                                    {itemdata.length > 0 ? (
                                        <>
                                            {itemdata.map((element, index) => {
                                                const relatedDebits = debitNoteMap[element.GRN] || [];

                                                return (
                                                    <React.Fragment key={index}>
                                                        {/* MAIN ROW */}
                                                        <tr className="border-t hover:bg-slate-50 transition">
                                                            <td className="px-3 py-2 whitespace-nowrap">
                                                                {formatLocalDate(element.ReceivedDate)}
                                                            </td>
                                                            <td className="px-3 py-2">{element.Company_Name}</td>
                                                            <td className="px-3 py-2">{element.UnitsOrdered}</td>
                                                            <td className="px-3 py-2">{formatSalesValue(element.Rate)}</td>
                                                            <td className="px-3 py-2 font-medium">
                                                                {formatSalesValue(element.Total)}
                                                            </td>
                                                            <td className="px-3 py-2">{element.GRN}</td>
                                                        </tr>

                                                        {/* DEBIT NOTES UNDER SAME GRN */}
                                                        {relatedDebits.map((note, i) => (
                                                            <tr
                                                                key={`debit-${i}`}
                                                                className="bg-red-50 text-red-700 border-t"
                                                            >
                                                                <td className="px-3 py-2">
                                                                    {formatLocalDate(note.debit_date)}
                                                                </td>
                                                                <td className="px-3 py-2">{note.Vendor}</td>

                                                                {/* NEGATIVE VALUES */}
                                                                <td className="px-3 py-2">
                                                                    -{parseFloat(note.Quantity)}
                                                                </td>

                                                                <td className="px-3 py-2">
                                                                    {formatSalesValue(note.unit_price)}
                                                                </td>

                                                                <td className="px-3 py-2 font-medium">
                                                                    -{formatSalesValue(note.total_amount)}
                                                                </td>

                                                                <td className="px-3 py-2">{note.GRN}</td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}

                                            {/* UNMATCHED DEBIT NOTES AT END */}
                                            {unmatchedDebitNotes.map((note, i) => (
                                                <tr key={`unmatched-${i}`} className="bg-red-100 text-red-700 border-t">
                                                    <td className="px-3 py-2">
                                                        {formatLocalDate(note.debit_date)}
                                                    </td>
                                                    <td className="px-3 py-2">Debit Note (Unmatched)</td>
                                                    <td className="px-3 py-2">
                                                        -{parseFloat(note.Quantity)}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {formatSalesValue(note.unit_price)}
                                                    </td>
                                                    <td className="px-3 py-2 font-medium">
                                                        -{formatSalesValue(note.total_amount)}
                                                    </td>
                                                    <td className="px-3 py-2">{note.GRN}</td>
                                                </tr>
                                            ))}
                                        </>
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-6 text-gray-500">
                                                Item data not available.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>

                                {/* Footer */}
                                {itemdata.length > 0 && (
                                    <tfoot className="bg-slate-50 sticky bottom-0">
                                        <tr className="border-t font-semibold">
                                            <td></td>
                                            <td></td>
                                            <td className="px-3 py-2">{totalQty}</td>
                                            <td></td>
                                            <td className="px-3 py-2">
                                                {formatSalesValue(totalAmount)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}

                            </table>
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className="text-base font-semibold text-[#003366]">
                        Stock Transfer
                    </h2>
                    {/* ================= RIGHT: COST CENTER TABLE ================= */}
                    <div className="bg-white shadow-md rounded-md border overflow-hidden">
                        <div className="overflow-x-auto overflow-y-auto max-h-[550px] tiny-scrollbar">
                            <table className="min-w-full text-sm text-center">

                                {/* Header */}
                                <thead className="bg-slate-100 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-3 py-2.5">Date</th>
                                        <th className="px-3 py-2.5">Cost Center</th>
                                        <th className="px-3 py-2.5">Qty</th>
                                        <th className="px-3 py-2.5">Rate</th>
                                        <th className="px-3 py-2.5">Total</th>
                                        <th className="px-3 py-2.5">Store Req ID</th>
                                    </tr>
                                </thead>

                                {/* Body */}
                                <tbody>
                                    {costCenters?.items?.length > 0 ? (
                                        costCenters.items.map((item, i) => (
                                            <tr key={i} className="border-t hover:bg-slate-50 transition">

                                                <td className="px-3 py-2">
                                                    {formatLocalDate(item.Date)}
                                                </td>

                                                <td className="px-3 py-2">
                                                    <button
                                                        className="text-blue-600 hover:underline"
                                                        onClick={() => {
                                                            setSelectedReq(item);
                                                            setNewCostCenter("");
                                                            setShowCostCenterModal(true);
                                                        }}
                                                    >
                                                        {item.CostCenter}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2">{item.Amount}</td>
                                                <td className="px-3 py-2">{formatSalesValue(item.Rate)}</td>

                                                <td className="px-3 py-2 font-medium">
                                                    {formatSalesValue(item.Total)}
                                                </td>

                                                <td className="px-3 py-2">{item.StoreReqID}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center py-6 text-gray-500">
                                                No cost center data available.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>

                                {/* Footer */}
                                {costCenters?.items?.length > 0 && (
                                    <tfoot className="bg-slate-50 sticky bottom-0">
                                        <tr className="border-t font-semibold">
                                            <td></td>
                                            <td></td>
                                            <td className="px-3 py-2">{costCenterQty}</td>
                                            <td></td>
                                            <td className="px-3 py-2">
                                                {formatSalesValue(costCenters.grand_total)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}

                            </table>
                        </div>
                    </div>
                </div>

            </div>

            <div className="px-10 mx-auto mb-4">
                {/* Charts Section */}
                <div className="bg-white shadow-md rounded-xl p-4 border space-y-3">
                    <h2 className="text-base font-semibold text-[#003366] border-b pb-2">
                        Item Analysis
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Line Chart */}
                        <div className="bg-slate-50 rounded-lg p-3 w-full h-[425px]">
                            <Line
                                data={chartData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                }}
                            />
                        </div>

                        {/* Bar Chart */}
                        <div className="bg-slate-50 rounded-lg p-3 w-full h-[425px]">
                            <Bar
                                data={barchartdata}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                }}
                            />
                        </div>

                    </div>
                </div>
            </div>

            {showCostCenterModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold mb-4 text-[#003366]">
                            Change Cost Center
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-500 mb-1">
                                    Store Req ID
                                </label>
                                <div className="font-medium">
                                    {selectedReq?.StoreReqID}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-500 mb-1">
                                    Current Cost Center
                                </label>
                                <input
                                    value={selectedReq?.CostCenter || ""}
                                    disabled
                                    className="w-full border rounded px-3 py-2 bg-gray-100 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-500 mb-1">
                                    New Cost Center
                                </label>
                                <input
                                    type="text"
                                    value={newCostCenter}
                                    onChange={(e) => setNewCostCenter(e.target.value)}
                                    placeholder="Enter new cost center"
                                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#003366]"
                                />
                            </div>
                        </div>

                        <div className="flex justify-between items-center mt-6 pt-4 border-t">
                            {/* Delete — left side */}
                            <button
                                onClick={deleteStockTransfer}
                                disabled={loading}
                                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 disabled:opacity-50 transition"
                            >
                                <MdDeleteOutline className="h-4 w-4" />
                                Delete
                            </button>

                            {/* Cancel + Update — right side */}
                            <div className="flex gap-2">
                                <button
                                    className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
                                    onClick={() => setShowCostCenterModal(false)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={updateCostCenter}
                                    disabled={loading || !newCostCenter?.trim()}
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-[#003366] text-white hover:bg-[#002244] disabled:opacity-50 transition"
                                >
                                    <MdAutorenew className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                                    {loading ? "Updating..." : "Update"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ItemListTable